import { ArrowLeft, Camera, ChevronRight } from "lucide-react";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";

import { api } from "@/api";
import { formatError, useAuth } from "@/auth";
import { GuideFiche } from "@/components/guide/GuideFiche";
import { pagesForPath, useGuide } from "@/components/guide/GuideContext";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";

function CapturesStatus({ captures, onRun }) {
  const errors = captures.errors || [];
  return (
    <div className="guide-captures" data-testid="guide-captures">
      <button type="button" className="secondary-button" disabled={captures.running} onClick={onRun} data-testid="guide-captures-run"><Camera size={14} />{captures.running ? `Captures en cours… ${captures.done || 0}/${captures.total || "?"}` : "Refaire toutes les captures"}</button>
      {!captures.running && captures.finished_at && <small data-testid="guide-captures-last">Dernières captures : {new Date(captures.finished_at).toLocaleString("fr-FR")}{errors.length ? ` · ${errors.length} en échec` : ""}</small>}
      {errors.length > 0 && <details><summary>Voir les échecs</summary><ul>{errors.map((e) => <li key={e}>{e}</li>)}</ul></details>}
    </div>
  );
}

function FicheList({ pages, here, onPick }) {
  const groups = [...new Set(pages.map((p) => p.group))];
  return (
    <div className="guide-list" data-testid="guide-list">
      {groups.map((g) => (
        <section key={g}>
          <h4>{g}</h4>
          {pages.filter((p) => p.group === g).map((p) => (
            <button key={p.slug} type="button" onClick={() => onPick(p.slug)} className={here.includes(p.slug) ? "is-here" : ""} data-testid={`guide-item-${p.slug}`}>
              <span>{p.title}{here.includes(p.slug) && <em> · cet écran</em>}</span><ChevronRight size={15} />
            </button>
          ))}
        </section>
      ))}
    </div>
  );
}

export function GuidePanel() {
  const guide = useGuide();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const admin = user?.role === "admin";
  const { pages, captures = {}, slug, open, setOpen, setSlug, load } = guide;
  useEffect(() => {
    if (!open || !captures.running) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [open, captures.running, load]);
  const runCaptures = () => api.post("/guide/captures").then(() => { toast.success("Captures lancées : cela prend quelques minutes."); load(); }).catch((e) => toast.error(formatError(e.response?.data?.detail)));
  const page = pages?.find((p) => p.slug === slug);
  const here = pagesForPath(pages, pathname).map((p) => p.slug);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="guide-sheet" data-testid="guide-panel">
        <SheetTitle className="guide-title">{page ? <button type="button" className="guide-back" onClick={() => setSlug(null)} data-testid="guide-back"><ArrowLeft size={15} />Toutes les fiches</button> : "Guide d'emploi"}</SheetTitle>
        <SheetDescription className="sr-only">Mode d'emploi et vérifications des fonctionnalités</SheetDescription>
        {!pages ? <p className="guide-muted">Chargement…</p> : page ? <GuideFiche page={page} admin={admin} onClose={() => setOpen(false)} onSaved={load} /> : (
          <>
            <p className="guide-muted">Une fiche par fonctionnalité : à quoi ça sert, comment l'utiliser, comment vérifier que ça marche, et les limites connues.</p>
            {admin && <CapturesStatus captures={captures} onRun={runCaptures} />}
            <FicheList pages={pages} here={here} onPick={setSlug} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
